import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { formatDuration } from '../../lib/replayUtils';

const SPEED_OPTIONS = [1, 2, 5, 10, 25, 50, 100, 150, 200, 400, 500];

@observer
class ReplayControls extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isDragging: false,
        };
        this.scrubberRef = null;
    }

    handlePlayPause = () => {
        const { onPlayPause } = this.props;
        if (onPlayPause) {
            onPlayPause();
        }
    }

    handleSpeedChange = (e) => {
        const { onSpeedChange } = this.props;
        const speed = parseInt(e.target.value, 10);
        if (onSpeedChange) {
            onSpeedChange(speed);
        }
    }

    handleClose = () => {
        const { onClose } = this.props;
        if (onClose) {
            onClose();
        }
    }

    getProgressPercent = () => {
        const { currentTime, startTime, endTime } = this.props;
        if (!startTime || !endTime || startTime === endTime) return 0;
        const progress = ((currentTime - startTime) / (endTime - startTime)) * 100;
        return Math.max(0, Math.min(100, progress));
    }

    getTimeFromPosition = (clientX) => {
        if (!this.scrubberRef) return null;
        const { startTime, endTime } = this.props;
        const rect = this.scrubberRef.getBoundingClientRect();
        const x = clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        return startTime + (endTime - startTime) * percent;
    }

    handleScrubberClick = (e) => {
        const { onSeek } = this.props;
        const newTime = this.getTimeFromPosition(e.clientX);
        if (newTime !== null && onSeek) {
            onSeek(newTime);
        }
    }

    handleDragStart = (e) => {
        e.preventDefault();
        this.setState({ isDragging: true });
        document.addEventListener('mousemove', this.handleDragMove);
        document.addEventListener('mouseup', this.handleDragEnd);
        document.addEventListener('touchmove', this.handleTouchMove);
        document.addEventListener('touchend', this.handleDragEnd);
    }

    handleDragMove = (e) => {
        if (!this.state.isDragging) return;
        const { onSeek } = this.props;
        const newTime = this.getTimeFromPosition(e.clientX);
        if (newTime !== null && onSeek) {
            onSeek(newTime);
        }
    }

    handleTouchMove = (e) => {
        if (!this.state.isDragging) return;
        const { onSeek } = this.props;
        const touch = e.touches[0];
        const newTime = this.getTimeFromPosition(touch.clientX);
        if (newTime !== null && onSeek) {
            onSeek(newTime);
        }
    }

    handleDragEnd = () => {
        this.setState({ isDragging: false });
        document.removeEventListener('mousemove', this.handleDragMove);
        document.removeEventListener('mouseup', this.handleDragEnd);
        document.removeEventListener('touchmove', this.handleTouchMove);
        document.removeEventListener('touchend', this.handleDragEnd);
    }

    handleTouchStart = (e) => {
        e.preventDefault();
        this.setState({ isDragging: true });
        document.addEventListener('touchmove', this.handleTouchMove);
        document.addEventListener('touchend', this.handleDragEnd);

        // Immediately seek to touch position
        const touch = e.touches[0];
        const { onSeek } = this.props;
        const newTime = this.getTimeFromPosition(touch.clientX);
        if (newTime !== null && onSeek) {
            onSeek(newTime);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleDragMove);
        document.removeEventListener('mouseup', this.handleDragEnd);
        document.removeEventListener('touchmove', this.handleTouchMove);
        document.removeEventListener('touchend', this.handleDragEnd);
    }

    render() {
        const { isPlaying, currentTime, startTime, endTime, speed, stores, bottomOffset = 0, isFixed = true } = this.props;
        const { isDragging } = this.state;
        const { userStore } = stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const progress = this.getProgressPercent();
        const elapsed = currentTime && startTime ? currentTime - startTime : 0;
        const total = endTime && startTime ? endTime - startTime : 0;

        // Calculate bottom position (above elevation graph if shown)
        const bottomPosition = 10 + bottomOffset;

        return (
            <div
                // When the map fills the viewport (isFixed), anchor to the viewport:
                // the position-relative wrapper has no in-flow content (the map is
                // position-fixed), so position-absolute + bottom lands off-screen
                class={`${isFixed ? 'position-fixed' : 'position-absolute'} d-flex flex-column rounded-lg px-2 py-1`}
                style={`
                    bottom: ${bottomPosition}px;
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 100001;
                    width: 92vw;
                    max-width: 500px;
                    background-color: rgba(0, 0, 0, 0.7);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                `}
            >
                {/* Header with time display */}
                <div class='d-flex flex-row justify-content-between align-items-center mb-1'>
                    <span class='font-weight-bold text-white' style='font-size: 0.8em;'>
                        <i class='fa-solid fa-backward me-1' />
                        Replay
                    </span>
                    <span class='text-white' style='font-size: 0.75em;'>
                        {formatDuration(elapsed)} / {formatDuration(total)}
                    </span>
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-light rounded-circle p-0'
                        onClick={this.handleClose}
                        style='width: 22px; height: 22px; line-height: 1;'
                    >
                        <i class='fa-solid fa-xmark' style='font-size: 0.7em;' />
                    </button>
                </div>

                {/* Progress Bar / Scrubber */}
                <div
                    ref={el => this.scrubberRef = el}
                    class='position-relative w-100 mb-1'
                    style={`height: 24px; cursor: pointer; touch-action: none;`}
                    onClick={this.handleScrubberClick}
                    onMouseDown={this.handleDragStart}
                    onTouchStart={this.handleTouchStart}
                >
                    {/* Track background */}
                    <div
                        class={`position-absolute rounded ${darkmode ? 'bg-secondary' : 'bg-light'}`}
                        style='height: 6px; top: 9px; left: 0; right: 0; opacity: 0.5;'
                    />
                    {/* Progress fill */}
                    <div
                        class='position-absolute bg-primary rounded'
                        style={`height: 6px; top: 9px; left: 0; width: ${progress}%;`}
                    />
                    {/* Scrubber handle */}
                    <div
                        class={`position-absolute bg-primary rounded-circle ${isDragging ? 'shadow' : ''}`}
                        style={`
                            width: 16px;
                            height: 16px;
                            top: 4px;
                            left: calc(${progress}% - 8px);
                            transition: ${isDragging ? 'none' : 'left 0.1s ease-out'};
                            ${isDragging ? 'transform: scale(1.2);' : ''}
                        `}
                    />
                </div>

                {/* Controls Row */}
                <div class='d-flex flex-row justify-content-between align-items-center'>
                    <button
                        type='button'
                        class={`btn btn-sm btn-${isPlaying ? 'warning' : 'success'} rounded-pill py-0`}
                        onClick={this.handlePlayPause}
                        style='min-width: 70px; font-size: 0.8em;'
                    >
                        <i class={`fa-solid fa-${isPlaying ? 'pause' : 'play'} me-1`} />
                        {isPlaying ? 'Pause' : 'Play'}
                    </button>

                    <div class='d-flex align-items-center'>
                        <span class='text-white me-1' style='font-size: 0.75em;'>Speed:</span>
                        <select
                            class={`form-select form-select-sm rounded-pill bg-dark text-white py-0`}
                            style='width: 65px; font-size: 0.75em;'
                            value={speed}
                            onChange={this.handleSpeedChange}
                            disabled={isPlaying}
                        >
                            {SPEED_OPTIONS.map(s => (
                                <option key={s} value={s}>{s}x</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        );
    }
}

export default ReplayControls;
